import { useState } from 'react';
import {
  Alert,
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useEligibleAppointmentCounselors } from '../../hooks/useEligibleAppointmentCounselors';
import { EligibleAppointmentCounselor } from '../../api/personalAppointments';

interface PersonalAppointmentDialogProps {
  open: boolean;
  errorMessage: string | null;
  onClose: () => void;
  onSubmit: (payload: { appointmentDate?: string; notes?: string; counselorIds?: number[] }) => Promise<void>;
}

/** "Convert to Personal Appointment" — the new required stage between an
 * approved Lead and starting the Student application (Lead -> PA ->
 * Application -> Documents -> Payment -> Student). The creating Counselor
 * is always the automatic, primary assignee (Phase 77); "Assign
 * Counselors" below only picks *additional* active Counselors in the
 * same branch — everything else (name/phone/source/branch) is already on
 * the Lead itself. */
export const PersonalAppointmentDialog = ({ open, errorMessage, onClose, onSubmit }: PersonalAppointmentDialogProps) => {
  const [appointmentDate, setAppointmentDate] = useState('');
  const [notes, setNotes] = useState('');
  const [counselors, setCounselors] = useState<EligibleAppointmentCounselor[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const { counselors: eligibleCounselors, isLoading: counselorsLoading } = useEligibleAppointmentCounselors(open);

  const reset = () => {
    setAppointmentDate('');
    setNotes('');
    setCounselors([]);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await onSubmit({
        appointmentDate: appointmentDate ? new Date(appointmentDate).toISOString() : undefined,
        notes: notes.trim() || undefined,
        counselorIds: counselors.map((c) => c.id),
      });
      reset();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>Convert to Personal Appointment</DialogTitle>
      <DialogContent>
        {errorMessage && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorMessage}
          </Alert>
        )}
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Appointment date/time"
            type="datetime-local"
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={appointmentDate}
            onChange={(e) => setAppointmentDate(e.target.value)}
          />
          <Stack spacing={0.5}>
            <Autocomplete
              multiple
              fullWidth
              size="small"
              loading={counselorsLoading}
              options={eligibleCounselors}
              getOptionLabel={(option) => `${option.firstName} ${option.lastName}`}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              value={counselors}
              onChange={(_e, newValue) => setCounselors(newValue)}
              renderInput={(params) => <TextField {...params} label="Assign additional counselors" />}
            />
            <Typography variant="caption" color="text.secondary">
              You are automatically assigned as the primary counselor.
            </Typography>
          </Stack>
          <TextField
            label="Notes"
            multiline
            minRows={3}
            fullWidth
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button variant="contained" disabled={submitting} onClick={handleSubmit}>
          {submitting ? 'Saving…' : 'Create Personal Appointment'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
